import { Icon } from "@iconify/react";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { getImageUrlHelper, truncateText } from "../../helper/helper";
import { processFileUpload, FILE_TYPES } from "../../utils/fileUploadHelper";
import {
  createConferenceAgenda,
  deleteConferenceAgenda,
  getConferenceAgenda,
  updateConferenceAgenda,
} from "../../redux/conferenceAgenda/conferenceAgendaSlice";

import useDebounce from "../../hook/useDebounce";
import { useLocationFilter } from "../../context/LocationContext";
import LocationSelectComponent from "../../components/common/LocationSelectComponent";
import PaginationHelper from "../../helper/PaginationHelper";

const initialState = {
  title: "",
  description: "",
  file: null,
  location: null,
};
const TITLE_MAX = 255;
const DESC_MAX = 255;

const ConferenceAgendaLayer = () => {
  const dispatch = useDispatch();
  const { selectedLocation } = useLocationFilter();

  const { agenda = [], total = 0 } = useSelector(
    (state) => state.agenda
  );

  const [search, setSearch] = useState("");
  const [entries, setEntries] = useState(50);
  const [currentPage, setCurrentPage] = useState(1);

  const [formData, setFormData] = useState(initialState);
  const [isEdit, setIsEdit] = useState(false);
  const [editId, setEditId] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const debouncedSearch = useDebounce(search, 1000);

  useEffect(() => {
    dispatch(
      getConferenceAgenda({
        page: currentPage,
        limit: entries,
        search: debouncedSearch,
        locationId: selectedLocation?.value || "",
      })
    );
  }, [dispatch, currentPage, entries, debouncedSearch, selectedLocation]);

  const totalPages = Math.ceil(total / entries);

  const openCreate = () => {
    setFormData(initialState);
    setImagePreview(null);
    setIsEdit(false);
  };

  const openEdit = (a) => {

    setFormData({
      title: a.title || "",
      description: a.description || "",
      location: a.locationId
        ? { value: a.locationId }
        : null,
      file: null,
    });

    setImagePreview(getImageUrlHelper(a.path));

    setEditId(a.id);
    setIsEdit(true);
  };

  const handleSubmit = () => {
    if (!formData.location) {
      toast.error("Location is required");
      return;
    }

    if (!formData.title || !formData.title.trim()) {
      toast.error("Title is required");
      return;
    }

    if (!formData.file && !isEdit) {
      toast.error("File is required");
      return;
    }
    if (formData.description && formData.description.length > DESC_MAX) {
      toast.error(`Description too long (max ${DESC_MAX} characters)`);
      return;
    }

    const fd = new FormData();
    fd.append("title", formData.title);
    fd.append("description", formData.description);
    fd.append("locationId", formData.location.value);
    if (formData.file) fd.append("file", formData.file);

    const action = isEdit
      ? updateConferenceAgenda({ id: editId, data: fd })
      : createConferenceAgenda(fd);

    dispatch(action)
      .unwrap()
      .then((res) => {
        const message =
          res?.message ||
          res?.data?.message ||
          "Operation successful";

        toast.success(message);

        dispatch(getConferenceAgenda({
          page: currentPage,
          limit: entries,
          search: debouncedSearch,
          locationId: selectedLocation?.value || "",
        }));

        document.querySelector("#agendaModal .btn-close")?.click();
      })
      .catch((err) => {
        const message =
          err?.response?.data?.message ||
          err?.message ||
          "Something went wrong";

        toast.error(message);
      });
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = [
      ...FILE_TYPES.IMAGE,
      ...FILE_TYPES.PDF,
    ];

    const processedFile = await processFileUpload(file, {
      allowedTypes,
    });

    if (!processedFile) return;

    setFormData({ ...formData, file: processedFile });

    if (processedFile.type.startsWith("image/")) {
      setImagePreview(URL.createObjectURL(processedFile));
    } else {
      setImagePreview(null);
    }
  };

  return (
    <>
      <div className="card h-100 p-0 radius-12">

        {/* HEADER SAME AS MEDIA */}
        <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">

          <div className="d-flex align-items-center gap-3">

            <span className="text-md fw-medium text-secondary-light">
              Show
            </span>

            <select
              className="form-select form-select-sm w-auto ps-12 py-6 radius-12 h-40-px"
              value={entries}
              onChange={(e) => {
                setEntries(Number(e.target.value));
                setCurrentPage(1);
              }}
            >
              <option value="50">50</option>
              <option value="100">100</option>
              <option value="150">150</option>
              <option value="200">200</option>
            </select>

            <form className="navbar-search">
              <input
                type="text"
                className="bg-base h-40-px w-auto"
                placeholder="Search"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setCurrentPage(1);
                }}
              />
              <Icon icon="ion:search-outline" className="icon" />
            </form>

          </div>

          <button
            className="btn btn-primary text-sm px-12 py-12 radius-8 d-flex align-items-center gap-2"
            data-bs-toggle="modal"
            data-bs-target="#agendaModal"
            onClick={openCreate}
          >
            <Icon icon="ic:baseline-plus" />
            Add Agenda
          </button>

        </div>

        {/* TABLE */}
        <div className="card-body p-12">
          <div className="table-responsive">

            <table className="table bordered-table sm-table">

              <thead>
                <tr>
                  <th>S.L</th>
                  <th>Image</th>
                  <th>Title</th>
                  <th>Description</th>
                  <th>Location</th>
                  <th className="text-center">Action</th>
                </tr>
              </thead>

              <tbody>
                {agenda.map((a, index) => (
                  <tr key={a.id}>
                    <td>{(currentPage - 1) * entries + index + 1}</td>

                    <td>
                      <img
                        src={getImageUrlHelper(a.path)}
                        className="w-40-px h-40-px radius-8 object-fit-cover"
                      />
                    </td>

                    <td>{truncateText(a.title, 30)}</td>
                    <td>{truncateText(a.description, 40)}</td>
                    <td>
                      {truncateText(a?.location?.name || "-", 30)}
                    </td>

                    <td className="text-center">
                      <div className="d-flex gap-10 justify-content-center">

                        <button
                          className="bg-success-focus text-success-600 w-40-px h-40-px rounded-circle d-flex align-items-center justify-content-center"
                          data-bs-toggle="modal"
                          data-bs-target="#agendaModal"
                          onClick={() => openEdit(a)}
                        >
                          <Icon icon="lucide:edit" />
                        </button>

                        <button
                          className="bg-danger-focus text-danger-600 w-40-px h-40-px rounded-circle d-flex align-items-center justify-content-center"
                          data-bs-toggle="modal"
                          data-bs-target="#deleteModal"
                          onClick={() => setDeleteId(a.id)}
                        >
                          <Icon icon="fluent:delete-24-regular" />
                        </button>

                      </div>
                    </td>

                  </tr>
                ))}
              </tbody>

            </table>

          </div>

          {/* PAGINATION */}
          <PaginationHelper
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            totalItems={total}
            pageSize={entries}
          />

        </div>
      </div>

      <div className="modal fade" id="agendaModal">
        <div className="modal-dialog modal-lg modal-dialog-centered">
          <div className="modal-content radius-16">

            <div className="modal-header">
              <h5>{isEdit ? "Update Agenda" : "Add Agenda"}</h5>
              <button className="btn-close" data-bs-dismiss="modal" />
            </div>

            <div className="modal-body">

              <div className="row">

                <div className="col-md-6 mb-3">
                  <label>Title <span className="text-danger">*</span></label>
                  <input
                    className="form-control"
                    value={formData.title}
                    onChange={(e) =>
                      setFormData({ ...formData, title: e.target.value })
                    }
                  />
                </div>
                {!isEdit && (
                  <div className="col-md-6 mb-3">
                    <LocationSelectComponent
                      label="Location"
                      required
                      value={formData.location}
                      onChange={(val) =>
                        setFormData({ ...formData, location: val })
                      }
                    />
                  </div>
                )}
                <div className="col-md-6 mb-3">
                  <label>Description <small className="fst-italic">(Max 255 chars)</small></label>
                  <textarea
                    className="form-control"
                    rows={5}
                    maxLength={255}
                    value={formData.description}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        description: e.target.value,
                      })
                    }
                  />


                </div>

                <div className="col-md-6 mb-3">
                  <label>Upload File <span className="text-danger">*</span></label>

                  <label
                    htmlFor="agendaUpload"
                    className="border radius-8 border-dashed d-flex align-items-center justify-content-center"
                    style={{ height: "150px", cursor: "pointer" }}
                  >
                    {imagePreview ? (
                      <img
                        src={imagePreview}
                        className="w-100 h-100 object-fit-contain radius-8"
                      />
                    ) : formData.file?.type === "application/pdf" ? (
                      <div className="text-center">
                        <Icon icon="mdi:file-pdf-box" width="40" color="red" />
                        <p>PDF Selected</p>
                      </div>
                    ) : (
                      <div className="text-center">
                        <Icon icon="solar:camera-outline" />
                        <p><small>(Only image & pdf allowed.)</small></p>
                      </div>
                    )}
                  </label>

                  <input
                    id="agendaUpload"
                    type="file"
                    hidden
                    accept=".jpg,.jpeg,.png,.webp,.pdf"
                    onChange={handleFileChange}
                  />

                </div>

              </div>

              <div className="text-end">
                <button className="btn btn-primary" onClick={handleSubmit}>
                  {isEdit ? "Update" : "Create"}
                </button>
              </div>

            </div>

          </div>
        </div>
      </div>

      {/* DELETE MODAL */}
      <div className="modal fade" id="deleteModal">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content radius-16">

            <div className="modal-header">
              <h5>Delete Agenda</h5>
              <button className="btn-close" data-bs-dismiss="modal" />
            </div>

            <div className="modal-body text-center">
              <p>Are you sure?</p>

              <button
                className="btn btn-danger"
                onClick={() => {
                  dispatch(deleteConferenceAgenda(deleteId))
                    .unwrap()
                    .then((res) => {
                      toast.success(res?.message || "Deleted successfully");
                      dispatch(getConferenceAgenda({ page: currentPage, limit: entries, search: debouncedSearch, locationId: selectedLocation?.value || "", }));
                      document.querySelector("#deleteModal .btn-close")?.click();
                    });
                }}
              >
                Delete
              </button>

            </div>

          </div>
        </div>
      </div>
    </>
  );
};

export default ConferenceAgendaLayer;